/**
 * Utility functions for processing message content
 */

export interface MessageMedia {
    type: 'image' | 'video';
    url: string;
    alt?: string;
}

export interface ProcessedMessagePart {
    text: string;
    media: MessageMedia[];
}

/**
 * Extract media (images and videos) from message content
 * @param content - The message content
 * @returns Object with cleaned text and extracted media
 */
export const extractMediaFromContent = (content: string): { text: string; media: MessageMedia[] } => {
    const media: MessageMedia[] = [];

    // Regex patterns for different media formats
    const imagePatterns = [
        // Markdown image format: ![alt](url)
        /!\[([^\]]*)\]\(([^)]+)\)/g,
        // HTML img tag: <img src="url" alt="alt" />
        /<img[^>]+src=["']([^"']+)["'][^>]*(?:alt=["']([^"']*)["'])?[^>]*\/?>/gi,
        // Direct image URLs (common extensions)
        /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg)(?:\?[^\s]*)?/gi
    ];

    const videoPatterns = [
        // Direct video URLs (common extensions)
        /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|avi|mov|wmv|flv|mkv)(?:\?[^\s]*)?/gi,
        // HTML video tag
        /<video[^>]+src=["']([^"']+)["'][^>]*>/gi
    ];

    let cleanedText = content;

    // Extract markdown images
    const markdownImageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const markdownMatches = content.match(markdownImageRegex) || [];
    markdownMatches.forEach(fullMatch => {
        const match = markdownImageRegex.exec(fullMatch);
        if (match) {
            media.push({
                type: 'image',
                url: match[2],
                alt: match[1] || undefined
            });
            cleanedText = cleanedText.replace(fullMatch, '');
        }
        markdownImageRegex.lastIndex = 0; // Reset regex
    });

    // Extract HTML img tags
    const htmlImageRegex = /<img[^>]+src=["']([^"']+)["'][^>]*(?:alt=["']([^"']*)["'])?[^>]*\/?>/gi;
    const htmlMatches = content.match(htmlImageRegex) || [];
    htmlMatches.forEach(fullMatch => {
        const match = htmlImageRegex.exec(fullMatch);
        if (match) {
            media.push({
                type: 'image',
                url: match[1],
                alt: match[2] || undefined
            });
            cleanedText = cleanedText.replace(fullMatch, '');
        }
        htmlImageRegex.lastIndex = 0; // Reset regex
    });

    // Extract direct image URLs
    const directImageRegex = /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg)(?:\?[^\s]*)?/gi;
    const directImageMatches = content.match(directImageRegex) || [];
    directImageMatches.forEach(url => {
        // Only add if not already captured by markdown or HTML patterns
        if (!media.some(m => m.url === url)) {
            media.push({
                type: 'image',
                url: url
            });
        }
        cleanedText = cleanedText.replace(url, '');
    });

    // Extract video URLs
    const directVideoRegex = /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|avi|mov|wmv|flv|mkv)(?:\?[^\s]*)?/gi;
    const directVideoMatches = content.match(directVideoRegex) || [];
    directVideoMatches.forEach(url => {
        media.push({
            type: 'video',
            url: url
        });
        cleanedText = cleanedText.replace(url, '');
    });

    // Extract HTML video tags
    const htmlVideoRegex = /<video[^>]+src=["']([^"']+)["'][^>]*>/gi;
    const htmlVideoMatches = content.match(htmlVideoRegex) || [];
    htmlVideoMatches.forEach(fullMatch => {
        const match = htmlVideoRegex.exec(fullMatch);
        if (match) {
            media.push({
                type: 'video',
                url: match[1]
            });
            cleanedText = cleanedText.replace(fullMatch, '');
        }
        htmlVideoRegex.lastIndex = 0; // Reset regex
    });

    // Clean up extra whitespace but preserve line breaks (\n)
    // Substituir múltiplos espaços por um único espaço, mas preservar \n
    cleanedText = cleanedText
      .replace(/[ \t]+/g, ' ') // Substituir múltiplos espaços/tabs por um espaço
      .replace(/[ \t]*\n[ \t]*/g, '\n') // Normalizar espaços ao redor de \n
      .trim();

    return { text: cleanedText, media };
};

/**
 * Simple fallback function for message splitting without media extraction
 * @param content - The original message content
 * @returns Array of simple message parts
 */
export const splitMessageContentSimple = (content: string): ProcessedMessagePart[] => {
    if (!content || typeof content !== 'string') {
        return [{ text: '', media: [] }];
    }

    const parts = content.split(/\[Mensagem \d+\]/);
    const filteredParts = parts
        .map(part => part.trim())
        .filter(part => part.length > 0);

    if (filteredParts.length === 0) {
        return [{ text: content, media: [] }];
    }

    return filteredParts.map(part => ({ text: part, media: [] }));
};

/**
 * Split message content by [Mensagem X] patterns into separate messages with media extraction
 * @param content - The original message content
 * @returns Array of processed message parts with text and media
 */
export const splitMessageContent = (content: string): ProcessedMessagePart[] => {
    // Handle empty or null content
    if (!content || typeof content !== 'string') {
        return [{ text: '', media: [] }];
    }

    try {
        // Split by [Mensagem X] patterns, where X is any number
        const parts = content.split(/\[Mensagem \d+\]/);

        // Process each part to extract media and clean text
        const processedParts = parts
            .map(part => part.trim())
            .filter(part => part.length > 0)
            .map(part => {
                const { text, media } = extractMediaFromContent(part);
                return { text, media };
            })
            .filter(part => part.text.length > 0 || part.media.length > 0);

        // If no parts were processed (e.g., content was just "[Mensagem 1]"), return the original content
        if (processedParts.length === 0) {
            const { text, media } = extractMediaFromContent(content);
            return [{ text, media }];
        }

        return processedParts;
    } catch (error) {
        console.error('Error in splitMessageContent, falling back to simple split:', error);
        return splitMessageContentSimple(content);
    }
};
